import { clone, isEqual, merge } from 'helpers/global.helper/global.helper'
import {
  applyAliases,
  applyPreviewEntry,
  mergeWithBaseUiConfig,
  translatedUiConfig,
} from 'helpers/uiConfig.helper/uiConfig.helper'
import {
  type ComputeUiConfigResolutionParams,
  type UiConfigResolutionResultStep,
  type UiConfigResolutionResultStepId,
} from 'helpers/uiConfig.helper/uiConfigResolutionPipeline.types'
import { applyDefaultTemplate, getDefaultChildrenIds } from 'ui/uiConfig/template/defaultUiConfig'

/**
 * Single orchestration point for runtime UiConfig resolution.
 *
 * This module exists to keep the store runtime path and the diagnostics/
 * inspector path in lockstep, so product behavior and debug explanations
 * cannot silently diverge.
 *
 * In short, the pipeline starts from the raw saved config, then applies
 * component defaults (when enabled), overlays in-memory preview edits,
 * resolves base_config/template relationships, expands aliases, and finally
 * applies runtime finishing rules (default children + translation).
 */

function isAliasPrimitive(value: unknown): value is UiConfigAliasPrimitive {
  return value === null || typeof value === 'string' || typeof value === 'number'
}

function isAliasValue(value: unknown): value is UiConfigAliasValue {
  if (isAliasPrimitive(value)) {
    return true
  }

  return Array.isArray(value) && value.every((entry) => isAliasPrimitive(entry))
}

function getAliasesFromProps(props: UiConfigProps | undefined): UiConfigAliases {
  const rawAlias = props?.alias
  if (!rawAlias || typeof rawAlias !== 'object' || Array.isArray(rawAlias)) {
    return {}
  }

  const normalizedAliases: UiConfigAliases = {}
  for (const [key, value] of Object.entries(rawAlias)) {
    if (isAliasValue(value)) {
      normalizedAliases[key] = value
    }
  }

  return normalizedAliases
}

/**
 * The aliases the pipeline's alias-expansion step resolves with: BaseConfig's `props.alias` (not its
 * top-level `alias`), with the caller's `overrideAlias` winning on a shared key, so a view can replace
 * a system default such as a subtype alias.
 */
export function getAliasesForAliasExpansion(
  baseUiConfig: UiConfig | null | undefined,
  overrideAlias: UiConfigAliases | undefined
): UiConfigAliases {
  return merge(clone(getAliasesFromProps(baseUiConfig?.props)), clone(overrideAlias || {}))
}

/**
 * Step snapshots are recorded only for semantic state transitions.
 *
 * This keeps the inspector focused on meaningful pipeline changes instead of
 * every stage invocation.
 */
function pushStepIfChanged(
  steps: UiConfigResolutionResultStep[],
  id: UiConfigResolutionResultStepId,
  current: UiConfig,
  next: UiConfig
): UiConfig {
  if (!isEqual(current, next)) {
    steps.push({
      id,
      uiConfig: clone(next),
    })
  }

  return next
}

function applyOverrideProps(current: UiConfig, overrideProps?: Record<string, unknown>): UiConfig {
  if (!overrideProps) {
    return current
  }

  const withOverrideProps = clone(current)
  Object.entries(overrideProps).forEach(([key, overrideValue]) => {
    withOverrideProps.props[key] = clone(overrideValue)
  })

  return withOverrideProps
}

/**
 * Applies intentionally post-resolution transforms.
 *
 * These are presentation/runtime finishing rules that run after core merging
 * and alias expansion have produced the resolved domain state.
 */
function applyDefaultChildrenAndTranslation(current: UiConfig): UiConfig {
  let finalUiConfig = clone(current)
  if (finalUiConfig.props?.use_default && finalUiConfig.component) {
    const defaultChildrenIds = getDefaultChildrenIds(finalUiConfig.component)
    finalUiConfig = {
      ...finalUiConfig,
      children_ids: [...(finalUiConfig.children_ids || []), ...defaultChildrenIds],
    }
  }

  return translatedUiConfig(finalUiConfig)
}

/**
 * Fast path for production rendering.
 *
 * Computes the resolved output without collecting intermediate snapshots.
 */
export function computeUiConfigResolvedOutput(params: ComputeUiConfigResolutionParams): UiConfig {
  const { rawUiConfig, baseUiConfig, previewCollection, overrideAlias, overrideProps } = params
  let current = clone(rawUiConfig)

  if (current.props?.use_default) {
    current = applyDefaultTemplate(clone(current))
  }

  const previewEntry = typeof current.uid === 'number' ? previewCollection?.[current.uid] : undefined
  if (previewEntry) {
    current = applyPreviewEntry(current, previewEntry)
  }
  current = applyOverrideProps(current, overrideProps)
  current = mergeWithBaseUiConfig(clone(current), baseUiConfig)

  current = applyAliases(clone(current), getAliasesForAliasExpansion(baseUiConfig, overrideAlias))

  return applyDefaultChildrenAndTranslation(current)
}

/**
 * Diagnostics path for explainability tooling.
 *
 * Computes the same resolved output as runtime while optionally emitting a
 * compact step trace.
 */
export function computeUiConfigResolutionResult(params: ComputeUiConfigResolutionParams): {
  finalUiConfig: UiConfig
  steps: UiConfigResolutionResultStep[]
} {
  const { rawUiConfig, baseUiConfig, previewCollection, overrideAlias, overrideProps, trackSteps = true } = params

  if (!trackSteps) {
    return {
      finalUiConfig: computeUiConfigResolvedOutput(params),
      steps: [],
    }
  }

  const steps: UiConfigResolutionResultStep[] = []
  let current = clone(rawUiConfig)

  steps.push({
    id: 'raw',
    uiConfig: clone(current),
  })

  if (current.props?.use_default) {
    current = pushStepIfChanged(steps, 'default_template', current, applyDefaultTemplate(clone(current)))
  }

  const previewEntry = typeof current.uid === 'number' ? previewCollection?.[current.uid] : undefined
  if (previewEntry) {
    current = pushStepIfChanged(steps, 'preview_overlay', current, applyPreviewEntry(current, previewEntry))
  }

  current = applyOverrideProps(current, overrideProps)

  current = pushStepIfChanged(steps, 'base_config_merge', current, mergeWithBaseUiConfig(clone(current), baseUiConfig))

  current = pushStepIfChanged(
    steps,
    'alias_expansion',
    current,
    applyAliases(clone(current), getAliasesForAliasExpansion(baseUiConfig, overrideAlias))
  )

  let finalUiConfig = applyDefaultChildrenAndTranslation(current)
  finalUiConfig = pushStepIfChanged(steps, 'default_children_and_translation', current, finalUiConfig)

  return {
    finalUiConfig,
    steps,
  }
}
